import React, { useState, useEffect } from 'react';
import { Chat } from '@google/genai';
import { UserData, ChatState, Message, Language } from './types';
import { createAstrologyChat, sendMessageToChat, generateChartData } from './services/geminiService';
import InputForm from './components/InputForm';
import ChatInterface from './components/ChatInterface';
import StarBackground from './components/StarBackground';

const App: React.FC = () => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [chatSession, setChatSession] = useState<Chat | null>(null);
  const [chatState, setChatState] = useState<ChatState>({
    messages: [],
    isLoading: false,
    isInitialized: false,
  });

  const handleFormSubmit = async (data: UserData) => {
    setUserData(data);
    setChatState(prev => ({ ...prev, isLoading: true }));

    try {
      // 1. Calculate Chart Data directly
      const [year, month, day] = data.birthDate.split('-').map(Number);
      const [hour, minute] = data.birthTime ? data.birthTime.split(':').map(Number) : [12, 0];
      const tzOffsetMinutes = 330; // +05:30
      const localTimeMs = Date.UTC(year, month - 1, day, hour, minute);
      const utcMs = localTimeMs - (tzOffsetMinutes * 60 * 1000);
      const dateObj = new Date(utcMs);
      
      const safeLanguage = data.language === '' ? Language.English : data.language;
      const computedData = generateChartData(dateObj, data.birthLocation, safeLanguage);

      // 2. Initialize the Chat Session with System Instructions
      const session = createAstrologyChat(data);
      setChatSession(session);

      // 3. Send the "invisible" trigger message to get the initial reading
      const initialPrompt = `Please generate my initial birth chart reading now in ${safeLanguage}.`;
      
      const responseText = await sendMessageToChat(session, initialPrompt);

      // 4. Update state with the model's response AND the chart data
      const initialMessage: Message = {
        id: crypto.randomUUID(),
        role: 'model',
        text: responseText,
        timestamp: new Date(),
        chartData: computedData.chartDataModel // Attach visual chart data
      };

      setChatState({
        messages: [initialMessage],
        isLoading: false,
        isInitialized: true,
      });

    } catch (error) {
      console.error("Initialization error:", error);
      // Vital: Set isInitialized to true so the user sees the error message in the chat interface
      setChatState(prev => ({ 
        ...prev, 
        isLoading: false, 
        isInitialized: true,
        messages: [
            ...prev.messages,
            {
                id: crypto.randomUUID(),
                role: 'model',
                text: `The cosmic energies are turbulent (Initialization Failed: ${error instanceof Error ? error.message : String(error)}). Please try refreshing or checking your input.`,
                timestamp: new Date(),
                isError: true
            }
        ] 
      }));
    }
  };

  const handleSendMessage = async (text: string) => {
    if (!chatSession) return;

    // Add user message to UI immediately
    const userMsg: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      text: text,
      timestamp: new Date(),
    };

    setChatState(prev => ({
      ...prev,
      messages: [...prev.messages, userMsg],
      isLoading: true,
    }));

    try {
      const responseText = await sendMessageToChat(chatSession, text);
      
      const modelMsg: Message = {
        id: crypto.randomUUID(),
        role: 'model',
        text: responseText,
        timestamp: new Date(),
      };

      setChatState(prev => ({
        ...prev,
        messages: [...prev.messages, modelMsg],
        isLoading: false,
        isInitialized: true,
      }));

    } catch (error) {
       console.error("Chat error:", error);
       setChatState(prev => ({
        ...prev,
        isLoading: false,
        messages: [...prev.messages, {
            id: crypto.randomUUID(),
            role: 'model',
            text: `Connection to the stars lost (${error instanceof Error ? error.message : String(error)}). Please try again.`,
            timestamp: new Date(),
            isError: true
        }],
      }));
    }
  };

  const handleReset = () => {
    setUserData(null);
    setChatSession(null);
    setChatState({
      messages: [],
      isLoading: false,
      isInitialized: false,
    });
  };

  return (
    <div className="min-h-screen font-sans text-gray-100 selection:bg-gold-500/30 selection:text-gold-200 relative">
      <StarBackground />
      
      {!chatState.isInitialized ? (
        <div className="flex items-center justify-center min-h-screen relative z-10 p-4">
          <InputForm onSubmit={handleFormSubmit} isLoading={chatState.isLoading} />
        </div>
      ) : (
        <ChatInterface 
            messages={chatState.messages} 
            onSendMessage={handleSendMessage} 
            isLoading={chatState.isLoading}
            onReset={handleReset}
        />
      )}

      {/* Footer */}
      <footer className="fixed bottom-2 right-4 z-50 pointer-events-none select-none">
        <p className="text-[10px] md:text-xs text-gold-500/40 font-serif tracking-widest drop-shadow-lg">
          Made by Kanth
        </p>
      </footer>
    </div>
  );
};

export default App;